import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { QueryWithExecutionRecord } from '../../../../../../API';
import { SegmentFilterNode, SegmentSelectedFilterConfig } from '../../../../models/market-fan-exploration.model';
import {
  LogicalOperator,
  SegmentConditionEntry,
  SegmentConditionGroup,
  SegmentConditionItem,
  SegmentHistoryRecord,
} from '../../../../models/history-model';

@Component({
  selector: 'app-segment-view-modal',
  templateUrl: './segment-view-modal.component.html',
  styleUrl: './segment-view-modal.component.scss',
})
export class SegmentViewModalComponent implements OnChanges {
  @Input() isVisible = false;
  @Input() isLoading = false;
  @Input() row: SegmentHistoryRecord | null = null;
  @Input() queryRecord: QueryWithExecutionRecord | null = null;
  @Input() queryText = '';
  @Output() close = new EventEmitter<void>();

  parsedTableName = '';
  parsedDatabaseName = '';
  parsedBaseTableName = '';
  parsedConditions: SegmentConditionGroup | null = null;

  private readonly sqlToUiOperatorMap: Record<string, string> = {
    '=': '==',
    '!=': '!=',
    '<>': '!=',
    LIKE: 'like',
    '<': '<',
    '>': '>',
    '<=': '<=',
    '>=': '>=',
    '<=>': '<=>',
    IN: 'in',
    'NOT IN': 'not in',
  };

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['queryText'] || changes['queryRecord'] || changes['row']) {
      this.rebuildSegmentPreview();
    }
  }

  get modalTitle(): string {
    return this.row ? `Segment Details: ${this.row.segmentName}` : 'Segment details';
  }

  get segmentName(): string {
    return this.row?.segmentName?.trim() || this.queryRecord?.queryName?.trim() || '-';
  }

  get segmentDescription(): string {
    return this.row?.segmentDescription?.trim() || this.queryRecord?.queryDescription?.trim() || '-';
  }

  get hasConditions(): boolean {
    return Boolean(this.parsedConditions?.entries.length);
  }

  trackByEntry(index: number, entry: SegmentConditionEntry): string {
    return `${entry.type}-${entry.joinWith || 'ROOT'}-${entry.row?.raw || index}`;
  }

  onClose(): void {
    this.close.emit();
  }

  private rebuildSegmentPreview(): void {
    const query = this.queryText?.trim() || this.queryRecord?.query?.trim() || '';
    const selectedFilterConfigRaw = this.queryRecord?.selectedFilterConfig?.trim() || '';

    if (!query && !selectedFilterConfigRaw) {
      this.parsedTableName = '';
      this.parsedDatabaseName = '';
      this.parsedBaseTableName = '';
      this.parsedConditions = null;
      return;
    }

    const selectedFilterConfig = this.parseSelectedFilterConfig(selectedFilterConfigRaw);

    if (selectedFilterConfig) {
      this.rebuildFromSelectedFilterConfig(selectedFilterConfig);
      return;
    }

    this.parsedTableName = this.extractTableName(query);
    [this.parsedDatabaseName, this.parsedBaseTableName] = this.splitTableName(this.parsedTableName);

    const whereClause = this.extractWhereClause(query);
    this.parsedConditions = whereClause ? this.parseGroup(whereClause) : null;
  }

  private rebuildFromSelectedFilterConfig(config: SegmentSelectedFilterConfig): void {
    this.parsedTableName = String(config.tableName || '').trim();
    [this.parsedDatabaseName, this.parsedBaseTableName] = this.splitTableName(this.parsedTableName);
    this.parsedConditions = {
      entries: this.mapFilterNodesToEntries(config.conditions || []),
    };
  }

  private mapFilterNodesToEntries(nodes: SegmentFilterNode[]): SegmentConditionEntry[] {
    const entries: SegmentConditionEntry[] = [];

    for (const node of nodes || []) {
      const joinWith = this.normalizeLogicalOperator(node?.operator);

      if (node?.condition) {
        entries.push({
          type: 'row',
          joinWith,
          row: {
            column: String(node.condition.colName || '-'),
            operator: this.toUiOperator(String(node.condition.operator || '')),
            value: String(node.condition.value || ''),
            raw: `${node.condition.colName || ''} ${node.condition.operator || ''} ${node.condition.value || ''}`.trim(),
          },
        });
        continue;
      }

      if (node?.group?.length) {
        entries.push({
          type: 'group',
          joinWith,
          group: {
            entries: this.mapFilterNodesToEntries(node.group),
          },
        });
      }
    }

    return entries;
  }

  private toUiOperator(operator: string): string {
    const normalized = operator.trim().toUpperCase();
    return this.sqlToUiOperatorMap[normalized] || this.sqlToUiOperatorMap[operator] || operator || '-';
  }

  private normalizeLogicalOperator(operator: string | undefined | null): LogicalOperator | undefined {
    const normalized = String(operator || '')
      .trim()
      .toUpperCase();
    return normalized === 'AND' || normalized === 'OR' ? normalized : undefined;
  }

  private parseSelectedFilterConfig(value: string): SegmentSelectedFilterConfig | null {
    if (!value) {
      return null;
    }

    const normalizedValue = value.trim();

    const parsedJson = this.tryParseJsonConfig(normalizedValue);
    if (parsedJson) {
      return parsedJson;
    }

    const parsedMap = this.tryParseMapLikeConfig(normalizedValue);
    if (!parsedMap || typeof parsedMap !== 'object') {
      return null;
    }

    return this.normalizeSelectedFilterConfigShape(parsedMap);
  }

  private tryParseJsonConfig(value: string): SegmentSelectedFilterConfig | null {
    try {
      const parsed = JSON.parse(value);
      return this.normalizeSelectedFilterConfigShape(parsed);
    } catch {
      return null;
    }
  }

  private tryParseMapLikeConfig(value: string): SegmentSelectedFilterConfig | null {
    try {
      const parser = new ConfigMapLikeParser(value);
      const parsed = parser.parseValue();
      return this.normalizeSelectedFilterConfigShape(parsed);
    } catch {
      return null;
    }
  }

  private normalizeSelectedFilterConfigShape(value: unknown): SegmentSelectedFilterConfig | null {
    if (!value || typeof value !== 'object') {
      return null;
    }

    const source = value as Record<string, unknown>;
    const tableName = String(source['tableName'] || '').trim();
    const rawConditions = source['conditions'];

    if (!tableName || !Array.isArray(rawConditions)) {
      return null;
    }

    return {
      tableName,
      conditions: this.normalizeFilterNodes(rawConditions),
    };
  }

  private normalizeFilterNodes(value: unknown[]): SegmentFilterNode[] {
    const nodes: SegmentFilterNode[] = [];

    for (const item of value) {
      if (!item || typeof item !== 'object') {
        continue;
      }

      const source = item as Record<string, unknown>;
      const operator = String(source['operator'] || '').trim();
      const node: SegmentFilterNode = { operator };

      if (source['condition'] && typeof source['condition'] === 'object') {
        const conditionSource = source['condition'] as Record<string, unknown>;
        node.condition = {
          colName: String(conditionSource['colName'] || '').trim(),
          operator: String(conditionSource['operator'] || '').trim(),
          value: String(conditionSource['value'] || '').trim(),
        };
      }

      if (Array.isArray(source['group'])) {
        node.group = this.normalizeFilterNodes(source['group'] as unknown[]);
      }

      if (node.condition || node.group?.length) {
        nodes.push(node);
      }
    }

    return nodes;
  }

  private extractTableName(query: string): string {
    const match = query.match(/\bfrom\s+([\w`".]+)/i);
    return match?.[1]?.replace(/["`]/g, '') || '';
  }

  private splitTableName(tableName: string): [string, string] {
    if (!tableName.includes('.')) {
      return ['', tableName || ''];
    }

    const parts = tableName.split('.').filter(Boolean);

    if (parts.length === 1) {
      return ['', parts[0]];
    }

    return [parts.slice(0, -1).join('.'), parts[parts.length - 1]];
  }

  private extractWhereClause(query: string): string {
    const whereMatch = query.match(/\bwhere\b([\s\S]*)$/i);

    if (!whereMatch?.[1]) {
      return '';
    }

    return whereMatch[1].trim().replace(/;\s*$/, '');
  }

  private parseGroup(expression: string): SegmentConditionGroup {
    const segments = this.splitTopLevelByLogicalOperator(this.trimOuterParens(expression));
    const entries: SegmentConditionEntry[] = [];

    for (const segment of segments) {
      const normalized = this.trimOuterParens(segment.text.trim());

      if (!normalized) {
        continue;
      }

      if (this.isWrappedBySingleParens(normalized)) {
        entries.push({
          type: 'group',
          joinWith: segment.joinWith,
          group: this.parseGroup(normalized),
        });
        continue;
      }

      entries.push({
        type: 'row',
        joinWith: segment.joinWith,
        row: this.parseCondition(normalized),
      });
    }

    return { entries };
  }

  private splitTopLevelByLogicalOperator(expression: string): {
    text: string;
    joinWith?: LogicalOperator;
  }[] {
    const segments: { text: string; joinWith?: LogicalOperator }[] = [];
    let current = '';
    let currentJoin: LogicalOperator | undefined;
    let depth = 0;
    let quote: string | null = null;

    for (let i = 0; i < expression.length; i++) {
      const char = expression[i];

      if (quote) {
        current += char;
        if (char === quote && expression[i - 1] !== '\\') {
          quote = null;
        }
        continue;
      }

      if (char === "'" || char === '"') {
        quote = char;
        current += char;
        continue;
      }

      if (char === '(') {
        depth++;
        current += char;
        continue;
      }

      if (char === ')') {
        depth = Math.max(0, depth - 1);
        current += char;
        continue;
      }

      if (depth === 0) {
        const remaining = expression.slice(i).toUpperCase();

        if (remaining.startsWith('AND') && this.isKeywordBoundary(expression, i, 3)) {
          if (current.trim()) {
            segments.push({ text: current.trim(), joinWith: currentJoin });
          }
          current = '';
          currentJoin = 'AND';
          i += 2;
          continue;
        }

        if (remaining.startsWith('OR') && this.isKeywordBoundary(expression, i, 2)) {
          if (current.trim()) {
            segments.push({ text: current.trim(), joinWith: currentJoin });
          }
          current = '';
          currentJoin = 'OR';
          i += 1;
          continue;
        }
      }

      current += char;
    }

    if (current.trim()) {
      segments.push({ text: current.trim(), joinWith: currentJoin });
    }

    return segments;
  }

  private isKeywordBoundary(expression: string, keywordStart: number, keywordLength: number): boolean {
    const before = expression[keywordStart - 1] ?? ' ';
    const after = expression[keywordStart + keywordLength] ?? ' ';
    return !/[A-Za-z0-9_]/.test(before) && !/[A-Za-z0-9_]/.test(after);
  }

  private isWrappedBySingleParens(value: string): boolean {
    const trimmed = value.trim();
    if (!(trimmed.startsWith('(') && trimmed.endsWith(')'))) {
      return false;
    }

    let depth = 0;
    let quote: string | null = null;

    for (let i = 0; i < trimmed.length; i++) {
      const char = trimmed[i];

      if (quote) {
        if (char === quote && trimmed[i - 1] !== '\\') {
          quote = null;
        }
        continue;
      }

      if (char === "'" || char === '"') {
        quote = char;
        continue;
      }

      if (char === '(') {
        depth++;
      } else if (char === ')') {
        depth--;
      }

      if (depth === 0 && i < trimmed.length - 1) {
        return false;
      }
    }

    return depth === 0;
  }

  private trimOuterParens(value: string): string {
    let result = value.trim();

    while (this.isWrappedBySingleParens(result)) {
      result = result.slice(1, -1).trim();
    }

    return result;
  }

  private parseCondition(conditionText: string): SegmentConditionItem {
    const cleanConditionText = conditionText.replace(/;\s*$/, '').trim();
    const match = cleanConditionText.match(/^\s*([\w.`"]+)\s*(NOT\s+IN|IN|LIKE|<=|>=|<=>|!=|<>|=|<|>)\s*(.+?)\s*$/i);

    if (!match) {
      return {
        column: '-',
        operator: '-',
        value: cleanConditionText,
        raw: cleanConditionText,
      };
    }

    const rawOperator = match[2].toUpperCase().replace(/\s+/g, ' ');
    const normalizedValue = this.normalizeConditionValue(match[3]);

    return {
      column: match[1].replace(/["`]/g, ''),
      operator: this.sqlToUiOperatorMap[rawOperator] || rawOperator,
      value: normalizedValue,
      raw: cleanConditionText,
    };
  }

  private normalizeConditionValue(value: string): string {
    const trimmed = value.trim().replace(/;\s*$/, '');

    if (trimmed.startsWith('(') && trimmed.endsWith(')')) {
      return trimmed.slice(1, -1).trim();
    }

    return trimmed.replace(/^['"]|['"]$/g, '');
  }
}

class ConfigMapLikeParser {
  private index = 0;

  constructor(private readonly input: string) {}

  parseValue(): unknown {
    this.skipWhitespace();
    const char = this.peek();

    if (char === '{') {
      return this.parseObject();
    }

    if (char === '[') {
      return this.parseArray();
    }

    return this.parseScalar();
  }

  private parseObject(): Record<string, unknown> {
    this.expect('{');
    const result: Record<string, unknown> = {};
    this.skipWhitespace();

    if (this.peek() === '}') {
      this.index++;
      return result;
    }

    while (this.index < this.input.length) {
      const key = this.parseKey();
      this.skipWhitespace();
      this.expect('=');
      const value = this.parseValue();
      result[key] = value;
      this.skipWhitespace();

      const next = this.peek();
      if (next === ',') {
        this.index++;
        this.skipWhitespace();
        continue;
      }

      if (next === '}') {
        this.index++;
        break;
      }

      throw new Error('Invalid object separator');
    }

    return result;
  }

  private parseArray(): unknown[] {
    this.expect('[');
    const result: unknown[] = [];
    this.skipWhitespace();

    if (this.peek() === ']') {
      this.index++;
      return result;
    }

    while (this.index < this.input.length) {
      result.push(this.parseValue());
      this.skipWhitespace();
      const next = this.peek();

      if (next === ',') {
        this.index++;
        this.skipWhitespace();
        continue;
      }

      if (next === ']') {
        this.index++;
        break;
      }

      throw new Error('Invalid array separator');
    }

    return result;
  }

  private parseKey(): string {
    this.skipWhitespace();
    const start = this.index;

    while (this.index < this.input.length) {
      const char = this.input[this.index];
      if (char === '=' || /\s/.test(char)) {
        break;
      }
      this.index++;
    }

    const key = this.input.slice(start, this.index).trim();
    if (!key) {
      throw new Error('Invalid key');
    }

    return key;
  }

  private parseScalar(): string {
    this.skipWhitespace();
    const start = this.index;
    let quote: string | null = null;

    while (this.index < this.input.length) {
      const char = this.input[this.index];

      if (quote) {
        if (char === quote && this.input[this.index - 1] !== '\\') {
          quote = null;
        }
        this.index++;
        continue;
      }

      if (char === "'" || char === '"') {
        quote = char;
        this.index++;
        continue;
      }

      if (char === ',' || char === ']' || char === '}') {
        break;
      }

      this.index++;
    }

    return this.input.slice(start, this.index).trim();
  }

  private skipWhitespace(): void {
    while (this.index < this.input.length && /\s/.test(this.input[this.index])) {
      this.index++;
    }
  }

  private expect(expected: string): void {
    this.skipWhitespace();
    if (this.peek() !== expected) {
      throw new Error(`Expected '${expected}'`);
    }
    this.index++;
  }

  private peek(): string {
    return this.input[this.index];
  }
}
